import { AdaptationCycleStageConfig } from '../types'
import { Landmark, Cog, Handshake } from 'lucide-react'
import adaptationCycleStageThreeImage from '@app/assets/illustrations/adaptation-cycle-overview-3.png'

export const adaptationCycleStageThree: AdaptationCycleStageConfig = {
  id: 'stage-three',
  title: 'Stage 3: Detailed Adaptation Planning',
  description:
    'Stage 3 is a cycle that is repeated for each focus area identified in Stage 2. Each cycle takes one focus area through risk assessment, options development, evaluation, and pathway design, producing an adopted adaptive strategy for that area. Councils, communities, mana whenua, and partners deliberate on options, trade-offs, timing, and triggers, using tools such as Dynamic Adaptive Pathways Planning (DAPP) or other context-appropriate methods. The order in which focus areas are addressed is determined by the prioritisation established in Stage 2, though organisations with sufficient capacity may run cycles concurrently.',
  tagline: '',
  heroImage: adaptationCycleStageThreeImage,

  outcome: {
    title: 'Outcome of Stage 3',
    description:
      'An adopted and implementable adaptive strategy for the focus area. The six activities are linked and iterative. The governance structure established in Activity 3.1 carries through all subsequent activities. Risk assessment (3.2) and options development (3.3) inform each other. Evaluation (3.4) and pathway design with monitoring (3.5) are explicitly iterative: the process should move between them as understanding develops, not treat them as sequential steps. Strategy adoption (3.6) closes the loop for each focus area and provides the formal basis for Stage 4.',
  },
  topParagraphs: [
    'A central challenge of this stage is about ensuring the end-result is implementable. Adaptation plans consistently fall short for a variety of reasons, for example strategies are not accepted, politically viable, or embedded in the institutions that carry out the work. This is the implementation gap, and it is important to address it directly and proactively. Deliberation processes, community validation, governance endorsement, and delivery handover that structure this stage are not nice-to-haves; they are often what separates a technically credible strategy from one that can withstand funding cycles, statutory constraints, and changing political, social and economic priorities.',
  ],
  activities: [
    {
      activity: 'Activity 3.1',
      forumSlug: 'focus-area-site-initiation',
      title: 'Initiate Focus Area Planning',
      mfeAlignment: [
        { step: 1, label: 'Set the context and prepare' },
        { step: 3, label: 'Establish values and objectives' },
      ],
      description:
        'This activity establishes the scope, governance structure, and engagement arrangements for adaptation planning within a specific focus area. It builds on the prioritisation and partnerships from Stages 1 and 2 to confirm who will be involved, what decisions they are being asked to make, and how the process will operate. The clarity established here carries through all subsequent activities in Stage 3.',
      streams: [
        // Keep to 3 streams
        {
          icon: Landmark,
          title: 'GOVERNANCE',
          points: [
            'Confirm the geographic scope and working boundaries of the focus area, recognising that boundaries may be contested or community-defined rather than hard geographic lines.',
            'Endorse the purpose and objectives of the focus area planning cycle, ensuring continuity with the programme objectives established in Stage 1.',
            'Endorse the proposed engagement approach and any representative or deliberative structures used.',
            'Approve initial budget and resourcing, including provision for community participation, administrative support, and accessible engagement delivery.',
          ],
        },
        {
          icon: Handshake,
          title: 'PARTNERSHIPS',
          points: [
            'Confirm representation of mana whenua, iwi, local organisations, community groups, and infrastructure partners in the panel or assembly.',
            'Establish clear terms for any formal engagement mechanism, covering purpose, role, participation, meeting or workshop protocols, and expectations where relevant.',
            'Confirm any honorarium or participation arrangements.',
            'Provide orientation for participants on the hazard context, process purpose, mandate, and expectations.',
            'Begin public-facing communication and community conversations: introduce the process, raise awareness of local hazards and their implications, and establish channels through which the wider community can engage throughout Stage 3.',
          ],
        },
        {
          icon: Cog,
          title: 'TECHNICAL',
          points: [
            'Confirm data and analysis requirements for the focus area, building on Stage 2 outputs.',
            'Align project timelines and resources with the wider programme.',
            'Determine the adaptive planning approach to be explored (Dynamic Adaptive Plan, Dynamic Adaptive Pathways Planning, or hybrid), noting that the final decision will be confirmed in Activity 3.6.',
          ],
        },
      ],
      outputs: [
        {
          text: 'Engagement arrangements for the focus area, including terms of reference or equivalent documentation where a formal engagement mechanism is used.',
        },
        {
          text: 'Initial communications and engagement plan, covering how hazard information will be made accessible, what channels will be used for broader engagement, and how community learning and deliberation will be supported.',
        },
        {
          text: 'Focus area initiation document, including scope and working boundaries of the focus area, purpose and objectives of the planning cycle linked to Stage 1 programme objectives, governance structure and engagement approach, and provisional adaptive planning approach.',
        },
      ],
      connections: [
        'Builds directly on focus area prioritisation (2.3) and the partnerships and relationships established in Stage 1.',
        'Establishes governance and engagement arrangements that persist through Stage 3.',
        'Sets expectations for community deliberation, risk tolerance discussions, and adaptive planning that carry into Activities 3.2 and 3.3.',
      ],
      relatedActivities: [
        {
          stageId: 'stage-two',
          activity: 'Activity 2.3',
          label: '2.3',
        },
        { stageId: 'stage-one', label: 'Stage 1' },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.2',
          label: '3.2',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.3',
          label: '3.3',
        },
      ],
      moveOnWhen: [
        'Scope and objectives are confirmed and linked to the wider programme.',
        'Engagement arrangements are established and participants understand their role.',
        'Mana whenua, community, and partner representation is confirmed and is representative of the focus area.',
        'Budget, resourcing, and any participation or honorarium arrangements are in place.',
      ],
      resources: [
        {
          title: 'Governance and Engagement Setup',
          articles: [
            {
              title: 'Community Engagement Principles',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-engagement-principles',
            },
          ],
        },
        {
          title: 'Māori and Community Partnership',
          articles: [
            {
              title: 'Engagement with Māori',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/engagement-with-maori',
            },
            {
              title: 'Mātauranga Māori (Māori Knowledge)',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/matauranga-maori-maori-knowledge',
            },
            {
              title: 'Protection of Māori Rights',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/protection-of-maori-rights',
            },
          ],
        },
        {
          title: 'Scope and Risk Context',
          articles: [
            {
              title: 'The Planning Process for Risk',
              description: '',
              href: '/app/guide/fundamentals/planning-and-implementation/the-planning-process-for-risk',
            },
            {
              title: 'Community Values in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-values-in-adaptation',
            },
            {
              title: 'Relational Risk, Values, and Worldviews',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/relational-risk-values-and-worldviews-how-values-shape-what-we-see-as-at-risk',
            },
          ],
        },
      ],
    },
    {
      activity: 'Activity 3.2',
      forumSlug: 'detailed-risk-assessment',
      title: 'Detailed Risk Assessment',
      mfeAlignment: [
        { step: 2, label: 'Assess sea-level rise and coastal hazards' },
        { step: 3, label: 'Establish values and objectives' },
        { step: 4, label: 'Assess vulnerability and risk' },
      ],
      description:
        'This activity develops a risk picture for the focus area. Technical hazard, exposure, and consequence analysis is combined with a focus area-level concern assessment so that community values, concerns, and lived experience shape the picture alongside technical evidence. The engagement approach established in Activity 3.1 provides the mechanism for deliberation and community input. The outputs feed directly into options development, evaluation, sequencing, and monitoring design. Activities 3.4 and 3.5 may identify specific evidence gaps, objectives, or thresholds that require the risk assessment to be refined.',
      streams: [
        // Keep to 3 streams
        {
          icon: Landmark,
          title: 'GOVERNANCE',
          points: [
            'Endorse the scope and objectives of the focus area risk assessment.',
            'Receive the completed risk assessment and confirm it is adequate to support options development.',
          ],
        },
        {
          icon: Handshake,
          title: 'PARTNERSHIPS',
          points: [
            'Prepare accessible materials that help participants and the wider community understand what hazard risks mean for them.',
            'Coordinate engagement to address hazard awareness and risk implications together, reducing unnecessary community touchpoints and grounding technical information in local experience.',
            'Facilitate structured engagement using technical risk outputs as a starting point for discussion, allowing community knowledge and concerns to test, refine, and contextualise the risk picture.',
            'Work with mana whenua, community participants, and other partners to translate qualitative values into community objectives that can be used consistently across options evaluation, sequencing, and monitoring design.',
          ],
        },
        {
          icon: Cog,
          title: 'TECHNICAL',
          points: [
            'Conduct detailed hazard, exposure, and consequence analysis for the focus area.',
            'Analyse risks across multiple hazard likelihoods and intensities, enabling the community to see at what level their values and functions would begin to be compromised.',
            'Identify preliminary thresholds where hazard intensity would compromise identified community values or critical functions.',
            'Integrate technical analysis with community objectives and values to produce a risk profile suitable for options development.',
            'Establish data structures that can be reused for future monitoring and reassessment.',
            'Refine the assessment where options evaluation or monitoring design identifies material evidence gaps, unclear thresholds, or indicators that cannot be measured reliably.',
          ],
        },
      ],
      outputs: [
        {
          text: 'Community values, objectives, and concerns documentation, produced through engagement with mana whenua, community participants, partners, and the wider community.',
        },
        {
          text: 'Technical risk profiles across multiple return periods and hazard increments, covering physical exposure and damage (expected annual damage, structural damage, isolation risk), social exposure and vulnerability (demographic characteristics, deprivation, access to resources), and economic exposure and vulnerability (land uses, key sectors, supply chains, industries).',
        },
        {
          text: 'Preliminary thresholds where hazard intensity compromises identified values or functions.',
        },
        {
          text: 'Integrated risk profile combining technical analysis and community perspectives, ready for use in options development.',
        },
      ],
      connections: [
        'Draws on hazard, exposure, and values information from Activities 2.1 and 2.2.',
        'Relies on the engagement arrangements established in Activity 3.1.',
        'The integrated risk profile informs options development (3.3), evaluation and sequencing (3.4), and signals, triggers, and monitoring (3.5).',
        'Activities 3.4 and 3.5 may return specific questions to this activity where evaluation requires additional consequence analysis or where preliminary thresholds need to be clarified or made measurable.',
        'Data structures established here support monitoring (4.2) and review (4.3).',
      ],
      relatedActivities: [
        {
          stageId: 'stage-two',
          activity: 'Activity 2.1',
          label: '2.1',
        },
        {
          stageId: 'stage-two',
          activity: 'Activity 2.2',
          label: '2.2',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.1',
          label: '3.1',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.3',
          label: '3.3',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.4',
          label: '3.4',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.5',
          label: '3.5',
        },
        {
          stageId: 'stage-four',
          activity: 'Activity 4.2',
          label: '4.2',
        },
        {
          stageId: 'stage-four',
          activity: 'Activity 4.3',
          label: '4.3',
        },
      ],
      moveOnWhen: [
        'Technical risk profiles are complete across relevant return periods and hazard scenarios.',
        'Community values and objectives have been documented and validated through the agreed engagement process, including with mana whenua and relevant partners.',
        'Preliminary risk thresholds are identified and sufficiently defined to support options evaluation and initial monitoring design.',
        'The risk profile integrates technical evidence with community values, concerns, and local knowledge.',
        'Known evidence gaps and uncertainties are documented for targeted refinement during Activities 3.4 and 3.5.',
      ],
      resources: [
        {
          title: 'Risk Concepts',
          articles: [
            {
              title: 'What Is Risk',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-risk',
            },
            {
              title: 'What Are Consequences',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-are-consequences',
            },
          ],
        },
        {
          title: 'Risk Tolerance and Acceptability',
          articles: [
            {
              title: 'Criticality and Importance',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-criticality',
            },
          ],
        },
        {
          title: 'Vulnerability and Consequences',
          articles: [
            {
              title: 'Vulnerability',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/vulnerability',
            },
            {
              title: 'Social Vulnerability',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/social-vulnerability',
            },
            {
              title: 'What Is Exposure',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-exposure',
            },
          ],
        },
        {
          title: 'Uncertainty and Scenarios',
          articles: [
            {
              title: 'Types of Uncertainty',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/types-of-uncertainty',
            },
            {
              title: 'Using Scenarios to Explore Uncertain Futures in Planning',
              description: '',
              href: '/app/guide/fundamentals/climate-futures-and-scenarios/using-scenarios-to-explore-uncertain-futures-in-planning',
            },
            {
              title: 'Sources of Uncertainty in Modelling',
              description: '',
              href: '/app/guide/fundamentals/evidence-and-confidence/uncertainty-in-modelling',
            },
            {
              title: 'Strength of Knowledge',
              description: '',
              href: '/app/guide/fundamentals/evidence-and-confidence/strength-of-knowledge',
            },
          ],
        },
        {
          title: 'Community and Values',
          articles: [
            {
              title: 'Community Values in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-values-in-adaptation',
            },
            {
              title: 'Relational Risk, Values, and Worldviews',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/relational-risk-values-and-worldviews-how-values-shape-what-we-see-as-at-risk',
            },
            {
              title: 'Foundations of Risk Perception',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/foundations-of-risk-perception',
            },
            {
              title: 'Foundations of Risk Communication',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/foundations-of-risk-communication',
            },
          ],
        },
      ],
    },
    {
      activity: 'Activity 3.3',
      forumSlug: 'options-development',
      title: 'Develop Options',
      mfeAlignment: [{ step: 5, label: 'Identify options and pathways' }],
      description:
        'This activity develops a shortlist of adaptation options by combining community and stakeholder ideas with technical expertise and screening them against agreed criteria. Community input occurs early, before options are screened, helping broaden the option space and surface locally relevant ideas. The shortlist is prepared in accessible formats for evaluation and deliberation in Activity 3.4.',
      streams: [
        // Keep to 3 streams
        {
          icon: Landmark,
          title: 'GOVERNANCE',
          points: [
            'Endorse the screening criteria before the long list is assessed.',
            'Receive the draft shortlist for awareness; formal endorsement follows evaluation and community deliberation in Activity 3.4.',
          ],
        },
        {
          icon: Handshake,
          title: 'PARTNERSHIPS',
          points: [
            'Facilitate early community and stakeholder input into potential adaptation options, using an engagement method appropriate to the focus area.',
            'Include community-generated options before screening so that local knowledge informs the long list and the process does not become a defence of predetermined options.',
            'Check shortlisted options against the community objectives established in Activity 3.2.',
            'Prepare plain-language descriptions and visual examples for each shortlisted option, sufficient to support informed deliberation in Activity 3.4.',
          ],
        },
        {
          icon: Cog,
          title: 'TECHNICAL',
          points: [
            'Draw on existing adaptation catalogues, case studies, and technical expertise to compile a candidate list of options.',
            'Build on existing resources rather than starting from scratch.',
            'Combine technical candidates with community brainstorm outputs to form a long list.',
            'The long list should span the full adaptation spectrum: avoid, retreat, accommodate, protect, accept.',
            'Screen the long list against agreed criteria: effectiveness (potential to reduce identified risks), feasibility (technical, financial, legal), environmental impact, timeframe, and alignment with community objectives, iwi and hapu criteria, and organisational guiding principles.',
            'Document rationale for all exclusions.',
            'Check that shortlisted options are coherent with the focus area risk profile from Activity 3.2.',
            'Options should be matched to the actual drivers and nature of risk identified, not assessed in the abstract.',
            'Where the risk profile identifies intolerable risks, ensure options capable of addressing them are retained in the shortlist.',
            'For risks in the tolerable range, apply proportionality: the level of intervention should be commensurate with the scale and nature of risk.',
            'Note options that need to work in combination to be effective, and flag where options are mutually exclusive or create path dependencies.',
          ],
        },
      ],
      outputs: [
        {
          text: 'Long list of adaptation options combining technical and community-generated options, with rationale for exclusions.',
        },
        {
          text: 'Shortlist of adaptation options, each covering: description, how the option addresses identified risks, indicative timeframe, and technical feasibility.',
        },
        {
          text: 'Plain-language descriptions and illustrations for each shortlisted option, prepared for Activity 3.4.',
        },
      ],
      connections: [
        'Builds on the focus area risk profile (3.2) and community values and objectives (2.2, 3.2).',
        'The shortlist feeds directly into options evaluation and community deliberation (3.4).',
        'Anticipates information requirements for feasibility, cost, and governance in Activity 3.6.',
      ],
      relatedActivities: [
        {
          stageId: 'stage-three',
          activity: 'Activity 3.2',
          label: '3.2',
        },
        {
          stageId: 'stage-two',
          activity: 'Activity 2.2',
          label: '2.2',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.4',
          label: '3.4',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.6',
          label: '3.6',
        },
      ],
      moveOnWhen: [
        'Long list is documented with transparent rationale for exclusions, combining technical expertise and community input.',
        'Shortlist is technically credible and addresses the identified risks of the focus area.',
        'Plain-language descriptions are ready for community engagement.',
      ],
      resources: [
        {
          title: 'Adaptation Options',
          articles: [
            {
              title: 'Risk Treatment',
              description: '',
              href: '/app/guide/fundamentals/adaptation-and-system-change/risk-treatment',
            },
            {
              title: 'Adaptive Capacity',
              description: '',
              href: '/app/guide/fundamentals/adaptation-and-system-change/adaptive-capacity',
            },
          ],
        },
        {
          title: 'Risk and Tolerability',
          articles: [
            {
              title: 'Risk & Surprises',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/risk-and-surprises-the-black-swan',
            },
          ],
        },
        {
          title: 'Community Co-design',
          articles: [
            {
              title: 'Community Engagement Principles',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-engagement-principles',
            },
            {
              title: 'Community Values in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-values-in-adaptation',
            },
            {
              title: 'Climate Justice in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/climate-justice-in-adaptation',
            },
          ],
        },
        {
          title: 'Further Reading and External Guidance',
          articles: [
            {
              title: 'Marae-opoly: A Serious Game to Support Adaptation Decision-Making',
              description: '',
              href: '/app/guide/insights/research-and-methods/marae-opoly-a-serious-game-to-support-adaptation-decision-making',
            },
          ],
        },
      ],
    },
    {
      activity: 'Activity 3.4',
      forumSlug: 'options-and-sequencing-evaluation',
      title: 'Evaluate Options and Sequences',
      mfeAlignment: [{ step: 6, label: 'Evaluate options and pathways' }],
      description:
        'This activity assesses how shortlisted options perform individually and in combinations or sequences against technical, community, and values-based criteria. This includes how far they reduce or redistribute risk, alongside costs, co-benefits, distributional effects, feasibility, and alignment with community objectives. Evaluating sequences makes dependencies, cumulative costs, trade-offs, co-benefits, and future constraints visible. The activity is iterative: evaluation may reveal that options need to be revised in Activity 3.3 or that aspects of the risk assessment, objectives, or thresholds in Activity 3.2 need to be refined. The outcome is a preferred near-term action and retained future options or sequences, with documented reasons, indicative costs, and funding assumptions. These provide the basis for signals, triggers, and monitoring in Activity 3.5.',
      streams: [
        // Keep to 3 streams
        {
          icon: Landmark,
          title: 'GOVERNANCE',
          points: [
            'Endorse the evaluation framework, including criteria, weighting, method, scenarios, and timeframes.',
            'Confirm the adaptive planning approach to be used, whether DAP, DAPP, hybrid, or another staged approach.',
            'Identify likely funding sources, responsible organisations, and dependencies on future budget or co-funding decisions.',
            'Review the evaluation findings and confirm the preferred near-term action and the future options or sequences to retain.',
            'Confirm the reasons for those choices and the intended approach to costs and funding.',
            'Reserve formal adoption for Activity 3.6.',
          ],
        },
        {
          icon: Handshake,
          title: 'PARTNERSHIPS',
          points: [
            'Prepare accessible comparison materials, including option scorecards, sequence diagrams, and trade-off visuals.',
            'Facilitate deliberation on how options and sequences align with community values, objectives, and guiding principles.',
            'Explore preferences about timing, sequencing, costs, benefits, and distributional impacts.',
            'Document how engagement and deliberation changed the options, evaluation, or preferred near-term action and retained future options.',
          ],
        },
        {
          icon: Cog,
          title: 'TECHNICAL',
          points: [
            'Apply multi-criteria evaluation across technical, social, cultural, economic, and environmental dimensions.',
            'Assess how each option, combination, or sequence changes risk, including residual risk and any risk shifted to other people, places, systems, or timescales.',
            'Identify adaptation tipping points where an option or sequence would no longer achieve agreed objectives or acceptable outcomes.',
            'Estimate indicative high-level costs and document assumptions, exclusions, and uncertainty.',
            'Provide the cost and sequencing information needed to assess affordability and funding requirements.',
            'Analyse benefits, co-benefits, trade-offs, affordability, distributional impacts, feasibility, and implementation constraints.',
            'Identify which options are complementary, dependent, mutually exclusive, or capable of being implemented independently.',
            'Develop and compare plausible option combinations and sequences across relevant scenarios and timeframes.',
            'Identify path dependencies and choices that may preserve or foreclose future options.',
            'Refine options, sequences, or the underlying risk assessment where evaluation identifies gaps or uncertainties.',
          ],
        },
      ],
      outputs: [
        {
          text: 'Evaluation framework covering criteria, weighting, method, scenarios, and timeframes.',
        },
        {
          text: 'Options evaluation matrix covering risk reduction, community objectives, indicative costs, benefits, co-benefits, and trade-offs.',
        },
        {
          text: 'Candidate option combinations or sequences showing dependencies, timing, and decision points.',
        },
        {
          text: 'Adaptation tipping points and associated decision points for the retained future options or sequences.',
        },
        {
          text: 'Comparative evaluation of candidate combinations or sequences.',
        },
        {
          text: 'Preferred near-term action, together with the future options or sequences to retain, with rationale.',
        },
        {
          text: 'Indicative cost and funding assessment for the preferred and retained options.',
        },
        {
          text: 'Documentation of community and stakeholder feedback and how it shaped the evaluation.',
        },
      ],
      connections: [
        'Evaluates options from Activity 3.3 using the risk profile, community objectives, and preliminary thresholds established in Activity 3.2.',
        'May return to Activity 3.2 where evaluation reveals missing consequences, unclear thresholds, or additional evidence needs.',
        'May return options to Activity 3.3 where evaluation identifies poor performance, gaps, or incompatibilities.',
        'Provides the preferred near-term action, retained future options or sequences, and decision points for signals, triggers, and monitoring in Activity 3.5.',
        'Informs strategy adoption in Activity 3.6.',
      ],
      relatedActivities: [
        {
          stageId: 'stage-three',
          activity: 'Activity 3.2',
          label: '3.2',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.3',
          label: '3.3',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.5',
          label: '3.5',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.6',
          label: '3.6',
        },
      ],
      moveOnWhen: [
        'Options and relevant combinations or sequences have been evaluated against agreed criteria.',
        'Risk reduction, costs, co-benefits, trade-offs, distributional impacts, feasibility, and future constraints are documented.',
        'Community and stakeholder feedback has been incorporated.',
        'A preferred near-term action and the future options or sequences to retain have been identified.',
        'Significant uncertainties and unresolved matters are recorded.',
      ],
      resources: [
        {
          title: 'Evaluation Frameworks',
          articles: [
            {
              title: 'Robust Decision Making (RDM)',
              description: '',
              href: '/app/guide/fundamentals/decision-making-and-evaluation/robust-decision-making',
            },
            {
              title: 'Real Options Analysis',
              description: '',
              href: '/app/guide/fundamentals/decision-making-and-evaluation/real-option-analysis',
            },
          ],
        },
        {
          title: 'Adaptive Planning',
          articles: [
            {
              title: 'Adaptive Capacity',
              description: '',
              href: '/app/guide/fundamentals/adaptation-and-system-change/adaptive-capacity',
            },
            {
              title: 'Dynamic Adaptive Policy Pathways (DAPP)',
              description: '',
              href: '/app/guide/fundamentals/decision-making-and-evaluation/dynamic-adaptive-pathways-planning',
            },
          ],
        },
        {
          title: 'Uncertainty and Scenarios',
          articles: [
            {
              title: 'Types of Uncertainty',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/types-of-uncertainty',
            },
            {
              title: 'Using Scenarios to Explore Uncertain Futures in Planning',
              description: '',
              href: '/app/guide/fundamentals/climate-futures-and-scenarios/using-scenarios-to-explore-uncertain-futures-in-planning',
            },
          ],
        },
        {
          title: 'Community Deliberation',
          articles: [
            {
              title: 'Community Values in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-values-in-adaptation',
            },
            {
              title: 'Relational Risk, Values, and Worldviews',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/relational-risk-values-and-worldviews-how-values-shape-what-we-see-as-at-risk',
            },
            {
              title: 'Heuristics and Cognitive Biases in Risk Decisions',
              description: '',
              href: '/app/guide/fundamentals/decisions-and-behaviour/heuristics-and-cognitive-biases',
            },
            {
              title: 'Risk Matrices: They Can Mislead More Than They Help',
              description: '',
              href: '/app/guide/insights/commentary-and-opinion/the-problem-with-risk-matrices',
            },
            {
              title: 'Marae-opoly: A Serious Game to Support Adaptation Decision-Making',
              description: '',
              href: '/app/guide/insights/research-and-methods/marae-opoly-a-serious-game-to-support-adaptation-decision-making',
            },
          ],
        },
      ],
    },
    {
      activity: 'Activity 3.5',
      forumSlug: 'monitoring-framework',
      title: 'Develop the Monitoring Framework',
      mfeAlignment: [
        { step: 7, label: 'Develop the adaptive planning strategy' },
        { step: 9, label: 'Monitor the adaptive planning strategy' },
      ],
      description:
        'This activity establishes the monitoring and decision framework needed to implement the preferred near-term action while retaining future options or sequences identified in Activity 3.4. It translates decision points and adaptation tipping points into measurable indicators, signals, triggers, post-event decision points, and review arrangements. Signals provide early warning that conditions are changing or a decision point is approaching. Triggers are predefined conditions that prompt a decision or action, allowing sufficient time to act before an adaptation tipping point is reached. Monitoring should be integrated with existing long-term planning, asset management, emergency management, budgeting, and organisational reporting processes wherever possible.',
      streams: [
        // Keep to 3 streams
        {
          icon: Landmark,
          title: 'GOVERNANCE',
          points: [
            'Review and endorse the monitoring framework for inclusion in the strategy adopted in Activity 3.6.',
            'Confirm responsibilities for monitoring, reporting, escalation, and review.',
            'Confirm how monitoring and review will connect with organisational decision cycles.',
          ],
        },
        {
          icon: Handshake,
          title: 'PARTNERSHIPS',
          points: [
            'Validate that indicators, signals, triggers, and post-event decision points reflect community, mana whenua, and partner perspectives.',
            'Include non-physical indicators where relevant, such as cultural impacts, wellbeing, access, place attachment, or community confidence.',
            'Test whether they are understandable to affected groups.',
            'Confirm how communities and partners will contribute to interpreting evidence and reviewing decisions.',
            'Ensure monitoring arrangements respect Te Tiriti obligations, data sovereignty, and partnership agreements.',
          ],
        },
        {
          icon: Cog,
          title: 'TECHNICAL',
          points: [
            'Translate the decision points and adaptation tipping points identified in Activity 3.4 into measurable indicators, signals, and triggers.',
            'Include physical, service, social, cultural, economic, financial, and institutional indicators where relevant.',
            "Specify each indicator's baseline, data source, measurement method, frequency, owner, and reporting mechanism.",
            'Define post-event decision points, including the consequences that require an option, sequence, tolerance threshold, or pathway to be reconsidered.',
            'Establish decision rules for escalation, including how conflicting evidence and data gaps will be handled.',
            'Define scheduled review points for reassessing assumptions, risk tolerance, option performance, and retained future options.',
            'Align monitoring with existing organisational processes.',
          ],
        },
      ],
      outputs: [
        {
          text: 'Monitoring framework linked to the decision points and adaptation tipping points identified in Activity 3.4.',
        },
        {
          text: 'Signals and triggers register covering indicators, baselines, thresholds, data sources, frequency, ownership, and reporting.',
        },
        {
          text: 'Post-event decision points and escalation protocol.',
        },
        {
          text: 'Monitoring and reporting plan integrated with relevant organisational cycles.',
        },
        {
          text: 'Updated sequencing documentation showing signals, triggers, and review points.',
        },
      ],
      connections: [
        'Applies the preferred near-term action, retained future options or sequences, and decision points identified in Activity 3.4.',
        'Operationalises the objectives, risk-tolerance thresholds, and levels of service established in Activities 3.1 and 3.2.',
        'May return to Activity 3.4 where monitoring design exposes weaknesses in the retained options or sequences.',
        'Provides the monitoring framework adopted in Activity 3.6 and implemented in Stage 4.',
        'Connects adaptation planning with organisational planning, investment, and reporting processes.',
      ],
      relatedActivities: [
        {
          stageId: 'stage-three',
          activity: 'Activity 3.1',
          label: '3.1',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.2',
          label: '3.2',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.4',
          label: '3.4',
        },
        {
          stageId: 'stage-three',
          activity: 'Activity 3.6',
          label: '3.6',
        },
        { stageId: 'stage-four', label: 'Stage 4' },
      ],
      moveOnWhen: [
        'Indicators, signals, triggers, and post-event decision points are defined for each material decision point.',
        'Responsibilities, data access, reporting arrangements, and resourcing are confirmed.',
        'Decision rules for escalation and review are documented.',
        'Community, mana whenua, and partner perspectives are reflected.',
        'Monitoring and review are integrated with relevant organisational cycles.',
      ],
      resources: [
        {
          title: 'Adaptive Planning',
          articles: [
            {
              title: 'Dynamic Adaptive Policy Pathways (DAPP)',
              description: '',
              href: '/app/guide/fundamentals/decision-making-and-evaluation/dynamic-adaptive-pathways-planning',
            },
            {
              title: 'Adaptive Capacity',
              description: '',
              href: '/app/guide/fundamentals/adaptation-and-system-change/adaptive-capacity',
            },
          ],
        },
        {
          title: 'Risk Thresholds and Tolerance',
          articles: [
            {
              title: 'Risk & Surprises',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/risk-and-surprises-the-black-swan',
            },
          ],
        },
        {
          title: 'Uncertainty and Evidence',
          articles: [
            {
              title: 'Types of Uncertainty',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/types-of-uncertainty',
            },
            {
              title: 'Using Scenarios to Explore Uncertain Futures in Planning',
              description: '',
              href: '/app/guide/fundamentals/climate-futures-and-scenarios/using-scenarios-to-explore-uncertain-futures-in-planning',
            },
            {
              title: 'Strength of Knowledge',
              description: '',
              href: '/app/guide/fundamentals/evidence-and-confidence/strength-of-knowledge',
            },
            {
              title: 'Sources of Uncertainty in Modelling',
              description: '',
              href: '/app/guide/fundamentals/evidence-and-confidence/uncertainty-in-modelling',
            },
          ],
        },
        {
          title: 'Community and Values',
          articles: [
            {
              title: 'Community Values in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-values-in-adaptation',
            },
            {
              title: 'Climate Justice in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/climate-justice-in-adaptation',
            },
          ],
        },
        {
          title: 'NZ Legislation and Policy',
          articles: [
            {
              title: 'NZ National Policy Statement for Natural Hazards 2025',
              description:
                'Establishes nationally consistent risk categories and management requirements for natural-hazard risk associated with subdivision, use and development.',
              href: 'https://environment.govt.nz/publications/national-policy-statement-for-natural-hazards/',
            },
          ],
        },
      ],
    },
    {
      activity: 'Activity 3.6',
      forumSlug: 'strategy-adoption',
      title: 'Adopt the Strategy',
      mfeAlignment: [
        { step: 7, label: 'Develop the adaptive planning strategy' },
        { step: 8, label: 'Implement the adaptive planning strategy' },
      ],
      description:
        'This activity finalises and adopts the focus area adaptive strategy. It confirms ownership and delivery responsibilities, establishes the financial and statutory pathway for implementation, and validates the strategy with community, mana whenua, and governance. Technical robustness and social legitimacy are necessary but not sufficient: the strategy also needs to be politically and financially actionable. If those conditions cannot be met, the process returns to earlier activities and iterates until operational commitments are secured.',
      streams: [
        // Keep to 3 streams
        {
          icon: Landmark,
          title: 'GOVERNANCE',
          points: [
            'Formally adopt the focus area adaptive strategy.',
            'Approve the implementation and accountability framework, confirming delivery responsibilities, monitoring arrangements, and reporting requirements.',
            'Confirm the indicative costs and financial implications of the preferred strategy.',
            'Endorse funding and financing principles, identify likely funding sources and responsible organisations, and confirm the pathway for incorporating actions into future budgets, Long-Term Plans, business cases, and co-funding processes.',
            'Confirm statutory integration pathways, mandating relevant teams (planning, asset management, iwi and hapu) to embed the strategy into applicable instruments such as district or regional plans, Long-Term Plans, and asset management plans.',
            'Approve public communication of the adopted strategy and its monitoring arrangements.',
            'For a statutory adaptation plan, approve the draft for consultation and adoption using the special consultative procedure.',
          ],
        },
        {
          icon: Handshake,
          title: 'PARTNERSHIPS',
          points: [
            'Co-validate the final strategy with iwi, mana whenua, community participants, and key partners through the agreed engagement process, confirming alignment with agreed values, principles, and process commitments.',
            'Confirm arrangements for ongoing participation and review, including how mana whenua, communities, and partners will contribute to monitoring, learning, and future iterations.',
            'Confirm that cultural, social, and institutional indicators are included in the monitoring framework so that non-tangible outcomes remain visible in Stage 4.',
            'Support communication and launch of the adopted strategy, ensuring messages reflect shared ownership and accountability.',
            'Support affected communities, iwi, mana whenua, infrastructure providers, and other partners to understand and respond to the proposed objectives, options, costs, triggers, implementation arrangements, and residual risks during formal consultation.',
          ],
        },
        {
          icon: Cog,
          title: 'TECHNICAL',
          points: [
            'Consolidate the indicative costs, assumptions, uncertainty, and funding dependencies identified in Activity 3.4 for inclusion in the final strategy.',
            'Identify where more detailed costing, design, or business-case development is required before implementation.',
            'Finalise the complete technical record of the focus area process (datasets, methods, assumptions, evaluation summaries) in formats suitable for statutory and organisational integration.',
            'Document and hand over implementation and monitoring responsibilities to relevant delivery teams.',
            'Clarity on triggers, data requirements, and reporting mechanisms is essential: delivery teams have separate budgets and competing priorities, and handover should not assume buy-in that has not been explicitly confirmed.',
            'Prepare concise public and decision-maker materials (maps, summaries, key results) that support adoption, communication, and transparency.',
            'Provide assurance documentation demonstrating that the strategy meets technical standards of reproducibility, transparency, and traceability.',
          ],
        },
      ],
      outputs: [
        {
          text: 'Adopted focus area adaptive strategy, including the pathway framework and triggers, confirmed ownership and delivery responsibilities, indicative costs, funding and financing principles, likely funding sources, and the pathway to future budget and business-case decisions.',
        },
        {
          text: 'Governance resolution adopting the strategy and confirming implementation arrangements.',
        },
        {
          text: 'Public-facing communication package.',
        },
        {
          text: 'Technical record and assurance documentation.',
        },
        {
          text: "Consultation record and decision report showing the submissions received, the authority's response, and any resulting changes to the plan.",
        },
      ],
      connections: [
        'Draws on the pathway framework and monitoring system from Activity 3.5 and ensures their social and institutional legitimacy through engagement and governance endorsement.',
        'Provides the formal output that feeds into implementation planning (4.1).',
        'Defines who will fund, deliver, and monitor actions, linking Stage 3 to Stage 4.',
      ],
      relatedActivities: [
        {
          stageId: 'stage-three',
          activity: 'Activity 3.5',
          label: '3.5',
        },
        {
          stageId: 'stage-four',
          activity: 'Activity 4.1',
          label: '4.1',
        },
        { stageId: 'stage-four', label: 'Stage 4' },
      ],
      moveOnWhen: [
        'Strategy is formally adopted by governance.',
        'Ownership, indicative costs, funding principles, and the pathway to future budget decisions are confirmed.',
        'Statutory integration is identified and mandated.',
        'Community and mana whenua co-validation is complete.',
        'Technical record and implementation handover are documented.',
      ],
      resources: [
        {
          title: 'Governance and Legitimacy',
          articles: [
            {
              title: 'Community Engagement Principles',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-engagement-principles',
            },
            {
              title: 'Central Government Roles in Resource Management',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/central-government-roles-in-resource-management',
            },
          ],
        },
        {
          title: 'Implementation',
          articles: [
            {
              title: 'Risk Treatment',
              description: '',
              href: '/app/guide/fundamentals/adaptation-and-system-change/risk-treatment',
            },
            {
              title: 'Adaptive Capacity',
              description: '',
              href: '/app/guide/fundamentals/adaptation-and-system-change/adaptive-capacity',
            },
            {
              title: 'Dynamic Adaptive Policy Pathways (DAPP)',
              description: '',
              href: '/app/guide/fundamentals/decision-making-and-evaluation/dynamic-adaptive-pathways-planning',
            },
          ],
        },
        {
          title: 'Māori Partnership and Values',
          articles: [
            {
              title: 'Engagement with Māori',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/engagement-with-maori',
            },
            {
              title: 'Protection of Māori Rights',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/protection-of-maori-rights',
            },
            {
              title: 'Climate Justice in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/climate-justice-in-adaptation',
            },
          ],
        },
      ],
    },
  ],
  previousStage: 'stage-two',
  nextStage: 'stage-four',
}
